import { Head, InfiniteScroll, router, usePage } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import {
  editTripPath,
  loginPath,
  newCelebrationPath,
  newTripPath,
  passportPath,
  profileFriendshipsPath,
  profilePath,
  profileStorePath,
  rootPath,
  tripPath,
} from "@travily/routes";
import type TripsRow from "@travily/types/models/TripsRow";
import type { TripsRowCanTrait } from "@travily/types/models/TripsRow";
import type IndividualsShow from "@travily/types/pages/IndividualsShow";
import { useConfirmDialog } from "@ui/components/ConfirmDialog";
import { SwooshBackground } from "@ui/components/Swoosh";
import { showToast } from "@ui/components/Toast";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { PROFILE_SWOOSH_COLORS } from "@/components/ProfileFrame/ProfileFrame";
import { useFriendshipActions } from "@/hooks/use-friendship-actions";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";
import { ShowView } from "./show.view";

type TripWithCan = TripsRow & TripsRowCanTrait;

const IndividualShowPage = ({
  individual,
  celebrations,
  trips,
  badges,
  can,
  has_store,
  restricted,
}: IndividualsShow) => {
  const { share } = useShare();
  const { visitModal } = useModalStack();
  const { current_account } = usePage<SharedProps>().props;
  const isOwnProfile = current_account?.handle === individual.handle;
  const onBack = useNavigateBack(rootPath());
  const friendshipActions = useFriendshipActions(individual);

  // Delete confirmation state — stays client-side (ephemeral UI)
  const deleteTrip = useConfirmDialog<string>(
    (sqid) =>
      new Promise<void>((resolve) => {
        router.delete(tripPath(sqid), { onFinish: () => resolve(), preserveScroll: true });
      }),
  );

  const handleShareProfile = () => {
    share({
      title: individual.display_name,
      text: t("web.individuals.show.share_text", { name: individual.display_name }),
      url: `${location.origin}${profilePath(individual.handle)}`,
    });
  };

  const handleViewFriends = () => {
    if (!current_account) {
      router.visit(loginPath());
      return;
    }
    // `restricted` is a per-request snapshot, so a profile that flipped private (or got renamed) since
    // render still 404s here — uncaught, that rejection escapes to window.onunhandledrejection.
    visitModal(profileFriendshipsPath(individual.handle)).catch(() =>
      showToast.error(t("common.friendships.action_error.title"), t("common.friendships.action_error.description")),
    );
  };

  const view = (
    <ShowView
      badges={badges}
      can={can}
      celebrations={celebrations}
      deleteState={deleteTrip.dialogProps}
      individual={individual}
      onAddCelebration={can.create_celebration ? () => visitModal(newCelebrationPath(individual.handle)) : undefined}
      onCreateTrip={can.create_trip ? () => visitModal(newTripPath()) : undefined}
      onDeleteTrip={deleteTrip.request}
      onEditTrip={(trip: TripWithCan) => visitModal(editTripPath(trip.sqid))}
      onShareProfile={handleShareProfile}
      onViewAllBadges={() => visitModal(passportPath(individual.handle))}
      onViewFriends={handleViewFriends}
      restricted={restricted}
      tabs={
        has_store
          ? { profileHref: profilePath(individual.handle), storeHref: profileStorePath(individual.handle) }
          : undefined
      }
      trips={trips}
      {...friendshipActions}
    />
  );

  return (
    <PageFrame
      background={<SwooshBackground className="hidden md:block" color={PROFILE_SWOOSH_COLORS.Individual} />}
      mobileTopBar={isOwnProfile ? undefined : <TopBarMobile onBack={onBack} title={`@${individual.handle}`} />}
    >
      <Head title={t("web.individuals.show.meta_title", { name: individual.display_name })}>
        <meta
          name="description"
          content={t("web.individuals.show.meta_description", { name: individual.display_name })}
        />
      </Head>
      <InfiniteScroll data="trips" preserveUrl onlyNext>
        {view}
      </InfiniteScroll>
    </PageFrame>
  );
};

export default IndividualShowPage;
